import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
  OnboardingState, ProfileKey, AreaKey, OnboardingVariant, VariantSource, OnboardingRoute,
} from './types';

const initialState: OnboardingState = {
  currentStep: 1,
  skippedUpload: false,
  skippedCategory: false,
  skippedReminder: false,
  startedAt: null,
  completedAt: null,
  profileKey: null,
  selectedAreas: [],
  sampleDemo: false,
  variant: null,
  variantSource: null,
  completedRoute: null,
};

const onboardingSlice = createSlice({
  name: 'onboarding',
  initialState,
  reducers: {
    startOnboarding: (state) => {
      state.startedAt = new Date().toISOString();
      state.currentStep = 1;
      state.completedAt = null;
    },
    setCurrentStep: (state, action: PayloadAction<number>) => {
      state.currentStep = action.payload;
    },
    setSkippedUpload: (state, action: PayloadAction<boolean>) => {
      state.skippedUpload = action.payload;
    },
    setSkippedCategory: (state, action: PayloadAction<boolean>) => {
      state.skippedCategory = action.payload;
    },
    setSkippedReminder: (state, action: PayloadAction<boolean>) => {
      state.skippedReminder = action.payload;
    },
    completeOnboarding: (state) => {
      state.completedAt = new Date().toISOString();
    },
    setProfileKey: (state, action: PayloadAction<ProfileKey>) => {
      state.profileKey = action.payload;
    },
    toggleArea: (state, action: PayloadAction<AreaKey>) => {
      // Defensive: redux-persist rehydrates this whitelisted slice as-is, so a
      // browser with onboarding state persisted before selectedAreas existed
      // lands here with the field missing entirely rather than `[]`.
      if (!state.selectedAreas) state.selectedAreas = [];
      const i = state.selectedAreas.indexOf(action.payload);
      if (i >= 0) state.selectedAreas.splice(i, 1);
      else state.selectedAreas.push(action.payload); // selection order = priority order
    },
    setSelectedAreas: (state, action: PayloadAction<AreaKey[]>) => {
      state.selectedAreas = action.payload;
    },
    setSampleDemo: (state, action: PayloadAction<boolean>) => {
      state.sampleDemo = action.payload;
    },
    setVariant: (state, action: PayloadAction<{ variant: OnboardingVariant; source: VariantSource }>) => {
      state.variant = action.payload.variant;
      state.variantSource = action.payload.source;
    },
    setCompletedRoute: (state, action: PayloadAction<OnboardingRoute | null>) => {
      state.completedRoute = action.payload;
    },
    // A fresh run clears everything except the variant: assignment is sticky
    // per device, so someone who comes back after leaving at the paywall sees
    // the same flow. `?? null` covers state persisted before these fields existed.
    resetOnboarding: (state) => ({
      ...initialState,
      variant: state.variant ?? null,
      variantSource: state.variantSource ?? null,
    }),
  },
});

export const {
  startOnboarding,
  setCurrentStep,
  setSkippedUpload,
  setSkippedCategory,
  setSkippedReminder,
  completeOnboarding,
  setProfileKey,
  toggleArea,
  setSelectedAreas,
  setSampleDemo,
  setVariant,
  setCompletedRoute,
  resetOnboarding,
} = onboardingSlice.actions;

// Selectors default missing fields to null: redux-persist rehydrates this
// slice as a whole, so a device persisted before these fields existed has
// them undefined rather than null.
type WithOnboarding = { onboarding: OnboardingState };
export const selectOnboardingVariant = (state: WithOnboarding) => state.onboarding.variant ?? null;
export const selectVariantSource = (state: WithOnboarding) => state.onboarding.variantSource ?? null;

// Which way through the add-a-document step this run went, for analytics:
// the sample demo, skipped upload into manual entry, or a real document.
// Only correct while the run is in progress: finishing a run clears the demo
// state, so a finished run reads the route recorded on completedRoute instead.
export type { OnboardingRoute };
export const deriveOnboardingRoute = (
  onboarding: Pick<OnboardingState, 'sampleDemo' | 'skippedUpload'> | undefined,
): OnboardingRoute => {
  if (onboarding?.sampleDemo) return 'demo';
  if (onboarding?.skippedUpload) return 'manual';
  return 'real';
};
// The route recorded when the run finished, falling back to the derived one
// while the run is still going (or for state persisted before route existed).
export const resolveOnboardingRoute = (
  onboarding: Pick<OnboardingState, 'sampleDemo' | 'skippedUpload' | 'completedRoute'> | undefined,
): OnboardingRoute => onboarding?.completedRoute ?? deriveOnboardingRoute(onboarding);
export const selectOnboardingRoute = (state: WithOnboarding) => resolveOnboardingRoute(state.onboarding);

export default onboardingSlice.reducer;
